import React, { useEffect, useRef, useState } from "react";
import StaffDetailHeader from "components/staff/StaffDetailHeader/StaffDetailHeader";
import showAlertNotification from "components/common/Modal/AlertNotification";
import { BasicInfoSection } from "components/staff/BasicInfoSection/BasicInfoSection";
import { ContactEmergencySection } from "components/staff/ContactEmergencySection/ContactEmergencySection";
import { IdentityTaxSection } from "components/staff/IdentityTaxSection/IdentityTaxSection";
import { InsuranceSection } from "components/staff/InsuranceSection/InsuranceSection";
import { DriverLicenseSection } from "components/staff/DriverLicenseSection/DriverLicenseSection";
import { Tabs } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ContractInfoTab from "./Tab/ContractInfoTab";
import DependentInfoTab from "./Tab/DependentInfoTab";
import {
  useGetDriverByIdQuery,
  useUpdateDriverMutation,
} from "services/driver.service";
import {
  useGetStaffByIdQuery,
  useUpdateStaffMutation,
} from "services/staff.service";
import { FileData, ImagePayload } from "types/image.type";
import { AccountSection } from "components/staff/AccountSection/AccountSection";

const initialFormData = {
  header: {
    fullName: "",
    role: "",
    staffCode: "",
    phoneNumber: "",
    department: "",
    avatarImage: "",
  },
  accountInfo: {
    fullName: "",
    email: "",
    isActive: true,
    avatarImage: [] as ImagePayload[],
  },
  basicInfo: {
    birthDate: "",
    nation: "",
    socialInsurance: { imagePath: "" },
    gender: "",
    ethnicity: "",
    maritalStatus: "",
    bankAccount: {
      accountNumber: "",
      bankName: "",
      bankLogo: [] as ImagePayload[],
    },
  },
  contactInfo: {
    phoneNumber: "",
    homePhone: "",
    currentAddress: "",
    permanentAddress: "",
  },
  emergencyInfo: {
    nationalityPhone: "",
    relationship: "",
  },
  identityTax: {
    identityNumber: "",
    taxCode: "",
    issuedDate: "",
    expiredDate: "",
    issuedPlace: "",
    identityImages: [] as ImagePayload[],
  },
  insuranceInfo: {
    socialInsuranceNumber: "",
    insuranceImages: [] as ImagePayload[],
  },
  driverLicense: {
    licenseClass: "",
    licenseNumber: "",
    licenseExpiredDate: "",
    vehicle: "",
    frontLicenseImages: [] as ImagePayload[],
    backLicenseImages: [] as ImagePayload[],
  },
};

type ContractTabHandle = {
  submitContract: () => Promise<void>;
};

const StaffDetailsEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(
    searchParams.get("tab") || "personal",
  );
  const employeeType =
    (searchParams.get("type") as "staff" | "driver") || "staff";

  const [formData, setFormData] = useState(initialFormData);
  const [isUploading, setIsUploading] = useState(false);

  const { data: driverRes } = useGetDriverByIdQuery(
    { id: id ?? "" },
    { skip: employeeType !== "driver" || !id },
  );
  const { data: staffRes } = useGetStaffByIdQuery(
    { id: id ?? "" },
    { skip: employeeType !== "staff" || !id },
  );

  const [updateStaff] = useUpdateStaffMutation();
  const [updateDriver] = useUpdateDriverMutation();

  useEffect(() => {
    const mapToImagePayload = (link: string, index: number) => ({
      filePath: link,
      url: link,
      name: `image-${index}`,
      order: index,
    });
    if (employeeType === "driver" && driverRes?.data) {
      const d = driverRes.data;
      setFormData((prev) => ({
        ...prev,
        header: {
          ...prev.header,
          fullName: d.user?.name || "",
          role: d.licenseClass ? `Tài xế hạng ${d.licenseClass}` : "Tài xế",
          staffCode: d.user?.code || "",
          phoneNumber: d.user?.phoneNumber || "",
          avatarImage: d.user?.avatarImage || "",
        },
        accountInfo: {
          ...prev.accountInfo,
          fullName: d.user?.name || "",
          email: d.user?.email || "",
          isActive: d.user?.isActive ?? true,
          avatarImage: [d.user?.avatarImage]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
        basicInfo: {
          ...prev.basicInfo,
          birthDate: d.user?.birthday || "",
          gender: d.user?.gender || "",
          maritalStatus: d.user?.maritalStatus || "",
          ethnicity: d.user?.ethnicity || "",
          nation: d.user?.nation || "",
          bankAccount: {
            accountNumber: d.user?.bankAccountNumber || "",
            bankName: d.user?.bankName || "",
            bankLogo: [d.user?.bankLogo]
              .filter((link): link is string => Boolean(link))
              .map(mapToImagePayload),
          },
        },
        contactInfo: {
          ...prev.contactInfo,
          phoneNumber: d.user?.phoneNumber || "",
          homePhone: d.user?.homePhone || "",
          currentAddress: d.user?.address || "",
          permanentAddress: d.user?.permanentAddress || "",
        },
        emergencyInfo: {
          nationalityPhone: d.user?.nationalityPhone || "",
          relationship: d.user?.emergencyRelationship || "",
        },
        identityTax: {
          ...prev.identityTax,
          identityNumber: d.user?.citizenId || "",
          taxCode: d.user?.taxCode || "",
          issuedDate: d.user?.citizenIdDate || "",
          expiredDate: d.user?.citizenIdExpiredDate || "",
          issuedPlace: d.user?.citizenIdPlace || "",
          identityImages: [d.user?.frontCitizenId, d.user?.backCitizenId]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
        insuranceInfo: {
          ...prev.insuranceInfo,
          socialInsuranceNumber: d.user?.socialInsuranceNumber || "",
          insuranceImages: [
            d.user?.frontSocialInsurance,
            d.user?.backSocialInsurance,
          ]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
        driverLicense: {
          ...prev.driverLicense,
          licenseClass: d.licenseClass || "",
          licenseNumber: d.licenseNumber || "",
          licenseExpiredDate: d.licenseExpiryDate || "",
          vehicle: d.mainVehicleType || "",
          frontLicenseImages: [d.frontLicenseImage]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
          backLicenseImages: [d.backLicenseImage]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
      }));
    } else if (employeeType === "staff" && staffRes?.data) {
      const s = staffRes.data;
      setFormData((prev) => ({
        ...prev,
        header: {
          ...prev.header,
          fullName: s.user?.name || "",
          role: s.positionTitle || "",
          staffCode: s.user?.code || "",
          phoneNumber: s.user?.phoneNumber || "",
          avatarImage: s.user?.avatarImage || "",
        },
        accountInfo: {
          ...prev.accountInfo,
          fullName: s.user?.name || "",
          email: s.user?.email || "",
          isActive: s.user?.isActive ?? true,
          avatarImage: [s.user?.avatarImage]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
        basicInfo: {
          ...prev.basicInfo,
          birthDate: s.user?.birthday || "",
          gender: s.user?.gender || "",
          maritalStatus: s.user?.maritalStatus || "",
          nation: s.user?.nation || "",
          ethnicity: s.user?.ethnicity || "",
          bankAccount: {
            accountNumber: s.user?.bankAccountNumber || "",
            bankName: s.user?.bankName || "",
            bankLogo: [s.user?.bankLogo]
              .filter((link): link is string => Boolean(link))
              .map(mapToImagePayload),
          },
        },
        contactInfo: {
          ...prev.contactInfo,
          phoneNumber: s.user?.phoneNumber || "",
          homePhone: s.user?.homePhone || "",
          currentAddress: s.user?.address || "",
          permanentAddress: s.user?.permanentAddress || "",
        },
        emergencyInfo: {
          nationalityPhone: s.user?.nationalityPhone || "",
          relationship: s.user?.emergencyRelationship || "",
        },
        identityTax: {
          ...prev.identityTax,
          identityNumber: s.user?.citizenId || "",
          taxCode: s.user?.taxCode || "",
          issuedDate: s.user?.citizenIdDate || "",
          expiredDate: s.user?.citizenIdExpiredDate || "",
          issuedPlace: s.user?.citizenIdPlace || "",
          identityImages: [s.user?.frontCitizenId, s.user?.backCitizenId]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
        insuranceInfo: {
          ...prev.insuranceInfo,
          socialInsuranceNumber: s.user?.socialInsuranceNumber || "",
          insuranceImages: [
            s.user?.frontSocialInsurance,
            s.user?.backSocialInsurance,
          ]
            .filter((link): link is string => Boolean(link))
            .map(mapToImagePayload),
        },
      }));
    }
  }, [driverRes, staffRes, employeeType]);

  const contractTabRef = useRef<ContractTabHandle | null>(null);

  const handleSaveContract = async () => {
    if (contractTabRef.current) {
       await contractTabRef.current.submitContract();
      navigate(-1);
    }
  };

  const dependentTabRef = useRef<{ submitAll: () => Promise<void> }>(null);

  const handleUpload = async (file: File, path: string) => {
    setIsUploading(true);
    try {
      const imageUrl = URL.createObjectURL(file);
      handleUpdate(path, imageUrl);

      showAlertNotification({
        type: "success",
        title: "Tải lên thành công",
        message: `Đã cập nhật ảnh: ${file.name}`,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploading(false);
    }
  };

  const extractImagePath = (
    images?: ImagePayload[],
    index: number = 0,
  ): string | undefined => {
    const img = images?.[index];
    if (!img) return undefined;

    if (typeof img === "string") {
      return img;
    }

    if (typeof img === "object" && img !== null) {
      const imgObj = img as unknown as Record<string, unknown>;
      return (imgObj.filePath as string) || (imgObj.url as string) || undefined;
    }

    return undefined;
  };

  const handleSavePersonal = async () => {
    if (!id) return;

    try {
      const genderMap: Record<string, string> = {
        Nam: "male",
        Nữ: "female",
        Khác: "other",
      };

      const userPayload = {
        name:
          formData.accountInfo.fullName ||
          formData.header.fullName ||
          undefined,
        email: formData.accountInfo.email || undefined,
        avatarImage: extractImagePath(formData.accountInfo.avatarImage, 0),

        phoneNumber: formData.contactInfo.phoneNumber || undefined,
        birthday: formData.basicInfo.birthDate || undefined,
        address: formData.contactInfo.currentAddress || undefined,
        permanentAddress: formData.contactInfo.permanentAddress || undefined,
        gender: formData.basicInfo.gender
          ? genderMap[formData.basicInfo.gender]
          : undefined,

        nation: formData.basicInfo.nation || undefined,
        ethnicity: formData.basicInfo.ethnicity || undefined,

        maritalStatus: formData.basicInfo.maritalStatus || undefined,
        citizenId: formData.identityTax.identityNumber || undefined,
        citizenIdDate: formData.identityTax.issuedDate || undefined,
        citizenIdPlace: formData.identityTax.issuedPlace || undefined,
        citizenIdExpiredDate: formData.identityTax.expiredDate || undefined,

        socialInsuranceNumber:
          formData.insuranceInfo.socialInsuranceNumber || undefined,
        taxCode: formData.identityTax.taxCode || undefined,
        homePhone: formData.contactInfo.homePhone || undefined,
        nationalityPhone: formData.emergencyInfo.nationalityPhone || undefined,
        emergencyRelationship: formData.emergencyInfo.relationship || undefined,
        bankAccountNumber:
          formData.basicInfo.bankAccount.accountNumber || undefined,
        bankName: formData.basicInfo.bankAccount.bankName || undefined,

        frontCitizenId: extractImagePath(
          formData.identityTax.identityImages,
          0,
        ),
        backCitizenId: extractImagePath(formData.identityTax.identityImages, 1),

        bankLogo: extractImagePath(formData.basicInfo.bankAccount.bankLogo, 0),

        frontSocialInsurance: extractImagePath(
          formData.insuranceInfo.insuranceImages,
          0,
        ),
        backSocialInsurance: extractImagePath(
          formData.insuranceInfo.insuranceImages,
          1,
        ),
      };

      if (employeeType === "driver") {
        const dataToSend = {
          id,
          driver: {
            licenseNumber: formData.driverLicense.licenseNumber || undefined,
            licenseClass: formData.driverLicense.licenseClass || undefined,
            licenseExpiryDate:
              formData.driverLicense.licenseExpiredDate || undefined,
            mainVehicleType: formData.driverLicense.vehicle || undefined,
            frontLicenseImage: extractImagePath(
              formData.driverLicense.frontLicenseImages,
              0,
            ),
            backLicenseImage: extractImagePath(
              formData.driverLicense.backLicenseImages,
              0,
            ),
          },
          user: userPayload,
        };
        await updateDriver(dataToSend).unwrap();
      } else {
        const dataToSend = {
          id,
          staff: {
            positionTitle: formData.header.role || undefined,
          },
          user: userPayload,
        };

        console.log("[DEBUG] DATA GỬI LÊN API (STAFF):", dataToSend);
        await updateStaff(dataToSend).unwrap();
      }

      showAlertNotification({
        type: "success",
        title: "Thành công",
        message: "Đã cập nhật thông tin nhân sự!",
      });
      navigate(-1);
    } catch (error) {
      showAlertNotification({
        type: "error",
        title: "Lỗi cập nhật",
        message: "Có lỗi xảy ra, vui lòng kiểm tra lại!",
      });
    }
  };

  const updateNestedState = <T extends object>(
    obj: T,
    path: string,
    value: string | FileData[] | ImagePayload[],
  ): T => {
    const keys = path.split(".");
    const newObj = { ...obj };
    let current: Record<string, unknown> = newObj as Record<string, unknown>;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      current[key] = Array.isArray(current[key])
        ? [...(current[key] as unknown[])]
        : { ...(current[key] as object) };
      current = current[key] as Record<string, unknown>;
    }
    current[keys[keys.length - 1]] = value;
    return newObj;
  };

  const handleUpdate = (
    path: string,
    value: string | FileData[] | ImagePayload[],
  ) => {
    setFormData((prev) => {
      const newData = updateNestedState(prev, path, value);
      return newData;
    });
  };

  const handleCancel = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/settings/staff");
    }
  };

  const handleEdit = () => {
    if (!id) return;
    navigate(`/settings/staff/${id}?type=${employeeType}`);
  };

  // Hàm đóng vai trò tổng chỉ huy cho nút Lưu ở Header
  const onSave = async () => {
    if (activeTab === "personal") {
      await handleSavePersonal();
    } else if (activeTab === "contract") {
      handleSaveContract();
    } else if (activeTab === "dependent") {
      if (dependentTabRef.current) {
        await dependentTabRef.current.submitAll();
        navigate(-1);
      }
    }
  };

  return (
    <>
      <div className="flex flex-col h-full">
        <StaffDetailHeader
          fullName={formData.header.fullName}
          role={formData.header.role}
          staffCode={formData.header.staffCode}
          phoneNumber={formData.header.phoneNumber}
          avatarImage={formData.header.avatarImage}
          department=""
          onSave={onSave}
          onCancel={handleCancel}
          onTerminate={() => alert("Hủy hợp tác")}
          onEdit={handleEdit}
          isEditing={true}
        />

        <div className="w-full px-1 py-3 pb-7">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setSearchParams((prev) => {
                prev.set("tab", key);
                return prev;
              });
            }}
            className="custom-tab"
            items={[
              {
                key: "personal",
                label: "Thông tin cá nhân",
                children: (
                  <div className="pb-0 bg-white mx-6 mb-0 pt-3 rounded-2xl">
                    <AccountSection
                      data={formData.accountInfo}
                      onUpdate={handleUpdate}
                      handleUpload={handleUpload}
                      isUploading={isUploading}
                      isEditing={!!id}
                    />
                    <BasicInfoSection
                      data={formData.basicInfo}
                      onUpdate={handleUpdate}
                      handleUpload={handleUpload}
                      isUploading={isUploading}
                    />
                    <ContactEmergencySection
                      contact={formData.contactInfo}
                      emergency={formData.emergencyInfo}
                      onUpdate={handleUpdate}
                    />
                    <IdentityTaxSection
                      data={formData.identityTax}
                      onUpdate={handleUpdate}
                      handleUpload={handleUpload}
                      isUploading={isUploading}
                    />
                    <InsuranceSection
                      data={formData.insuranceInfo}
                      onUpdate={handleUpdate}
                      handleUpload={handleUpload}
                      isUploading={isUploading}
                    />
                    {employeeType === "driver" && (
                      <DriverLicenseSection
                        data={formData.driverLicense}
                        onUpdate={handleUpdate}
                        handleUpload={handleUpload}
                        isUploading={isUploading}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: "contract",
                label: "Thông tin hợp đồng",
                children: (
                  <ContractInfoTab isEditing={true} ref={contractTabRef} />
                ),
              },
              {
                key: "dependent",
                label: "Thông tin NPT",
                children: (
                  <DependentInfoTab isEditing={true} ref={dependentTabRef} />
                ),
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};

export default StaffDetailsEdit;
